import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createBankAccount,
  deleteBankAccount,
  fetchBankAccounts,
  updateBankAccount,
} from '../../api/salon';
import Input from '../ui/Input';
import { useToast } from '../../context/ToastContext';

const emptyForm = {
  bankName: '',
  accountName: '',
  accountNumber: '',
  branchName: '',
};

const BankAccountsManager = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['salon-bank-accounts'],
    queryFn: fetchBankAccounts,
  });

  const accounts = data?.bankAccounts || [];

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['salon-bank-accounts'] });
    queryClient.invalidateQueries({ queryKey: ['salon-profile'] });
  };

  const saveMut = useMutation({
    mutationFn: () =>
      editingId
        ? updateBankAccount(editingId, form)
        : createBankAccount(form),
    onSuccess: () => {
      showToast(t(editingId ? 'payments.bank.updated' : 'payments.bank.added'), 'success');
      setForm(emptyForm);
      setEditingId(null);
      invalidate();
    },
    onError: (err) => showToast(err.response?.data?.message || t('errors.saveFailed'), 'error'),
  });

  const deactivateMut = useMutation({
    mutationFn: (id) => deleteBankAccount(id),
    onSuccess: () => {
      showToast(t('payments.bank.removed'), 'success');
      if (editingId) {
        setEditingId(null);
        setForm(emptyForm);
      }
      invalidate();
    },
    onError: (err) => showToast(err.response?.data?.message || t('errors.saveFailed'), 'error'),
  });

  const startEdit = (account) => {
    setEditingId(account._id);
    setForm({
      bankName: account.bankName,
      accountName: account.accountName,
      accountNumber: account.accountNumber,
      branchName: account.branchName || '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  return (
    <div className="max-w-xl space-y-4 rounded-xl border border-zinc-200 dark:border-zinc-800 p-5">
      <div>
        <h2 className="text-lg font-medium">{t('payments.bank.title')}</h2>
        <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-500">{t('payments.bank.ownerHint')}</p>
      </div>

      {isLoading && <p className="text-sm text-zinc-500">{t('common.loading')}</p>}

      {accounts.filter((a) => a.isActive).map((account) => (
        <div
          key={account._id}
          className="flex items-start justify-between gap-3 rounded-lg border border-zinc-200 dark:border-zinc-800 p-3"
        >
          <div className="text-sm">
            <p className="font-medium">{account.bankName}</p>
            <p className="text-zinc-600 dark:text-zinc-400">{account.accountName}</p>
            <p className="font-mono text-xs">{account.accountNumber}</p>
            {account.branchName && <p className="text-xs text-zinc-500">{account.branchName}</p>}
          </div>
          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              onClick={() => startEdit(account)}
              className="text-xs text-glow-400 hover:underline"
            >
              {t('common.edit')}
            </button>
            <button
              type="button"
              onClick={() => deactivateMut.mutate(account._id)}
              className="text-xs text-red-400 hover:underline"
            >
              {t('common.delete')}
            </button>
          </div>
        </div>
      ))}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          saveMut.mutate();
        }}
        className="space-y-3 border-t border-zinc-200 pt-4 dark:border-zinc-800"
      >
        <p className="text-sm font-medium">
          {editingId ? t('payments.bank.editAccount') : t('payments.bank.addAccount')}
        </p>
        <Input
          label={t('payments.bank.bankName')}
          id="bankName"
          value={form.bankName}
          onChange={(e) => setForm((f) => ({ ...f, bankName: e.target.value }))}
          required
        />
        <Input
          label={t('payments.bank.accountName')}
          id="accountName"
          value={form.accountName}
          onChange={(e) => setForm((f) => ({ ...f, accountName: e.target.value }))}
          required
        />
        <Input
          label={t('payments.bank.accountNumber')}
          id="accountNumber"
          value={form.accountNumber}
          onChange={(e) => setForm((f) => ({ ...f, accountNumber: e.target.value }))}
          required
        />
        <Input
          label={t('payments.bank.branchName')}
          id="branchName"
          value={form.branchName}
          onChange={(e) => setForm((f) => ({ ...f, branchName: e.target.value }))}
        />
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={saveMut.isPending}
            className="rounded-lg bg-glow-600 px-4 py-2 text-sm text-white disabled:opacity-50"
          >
            {saveMut.isPending ? t('common.saving') : editingId ? t('common.saveChanges') : t('payments.bank.addAccount')}
          </button>
          {editingId && (
            <button type="button" onClick={cancelEdit} className="rounded-lg border border-zinc-300 px-4 py-2 text-sm dark:border-zinc-700">
              {t('common.cancel')}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default BankAccountsManager;
